/**
 * UNIFIED SHARE CALCULATION SERVICE
 * ==================================
 * 
 * This is the SINGLE SOURCE OF TRUTH for calculating user share balances.
 * ALL components, services, APIs, and views MUST use this service.
 * 
 * DO NOT calculate shares anywhere else - import this service instead!
 * 
 * SHARE SOURCES (based on transaction records):
 * 1. Direct Purchases: aureus_share_purchases WHERE status='active' AND payment_method NOT LIKE 'COMMISSION_LEVEL%'
 * 2. Bonus Shares: aureus_share_purchases.bonus_shares WHERE status='active'
 * 3. Commission Level Shares: aureus_share_purchases WHERE status='active' AND payment_method LIKE 'COMMISSION_LEVEL%'
 * 4. Presale Commission Shares: commission_transactions.share_commission WHERE referrer_id=user AND status='approved'
 * 5. Converted Shares: commission_conversions.shares_requested WHERE status='approved'
 * 6. Transfers Received: share_transfers.shares_transferred WHERE recipient_user_id=user AND status='completed'
 * 7. Transfers Sent (DEDUCTION): share_transfers.shares_transferred WHERE sender_user_id=user AND status='completed'
 * 
 * FORMULA:
 * Total Shares = DirectPurchases + BonusShares + CommissionLevelShares + PresaleCommissionShares + ConvertedShares + TransfersReceived - TransfersSent + LegacyShares
 *
 * NOTE: We do NOT use commission_balances.total_earned_shares as it may be out of sync.
 * Instead, we calculate directly from transaction records (source of truth).
 *
 * LEGACY SHARES: This field in commission_balances stores historical share adjustments
 * from the pre-fix era when conversion bonus bugs and double-counting inflated balances.
 * These are preserved to honor what users were previously shown.
 */

import { getSessionHeaders } from '../serverSession';

/**
 * SECURITY: All DB access goes through server-side API - NO getServiceRoleClient() in browser
 */
async function callShareCalcAPI(operation: string, params: any = {}): Promise<any> {
 const response = await fetch('/api/user/share-calculation', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ operation, ...params }),
 });
 if (!response.ok) {
 const errorData = await response.json().catch(() => ({}));
 throw new Error(errorData.error || `API error: ${response.status}`);
 }
 return response.json();
}

export interface ShareBreakdown {
 // Core share sources
 directPurchases: number; // Cash/crypto purchases (non-COMMISSION_LEVEL)
 bonusShares: number; // Tier bonus shares from aureus_share_purchases.bonus_shares
 commissionLevelShares: number; // Multi-level commission shares (COMMISSION_LEVEL_X)
 presaleCommissionShares: number; // Phase 0 presale 15% bonus (commission_transactions.share_commission)
 convertedShares: number; // USDT commission converted to shares

 // Transfers
 transfersReceived: number;
 transfersSent: number;
 netTransfers: number; // received - sent

 // Legacy adjustment (historical corrections)
 legacyShares: number; // Pre-fix era adjustments preserved for users

 // Totals
 totalShares: number; // Final total (excludes fractional remainders)
 wholeShares: number; // Floor of totalShares
 fractionalShares: number; // Decimal portion (pre-consolidation leftovers in source tables)
 fractionalRemainderTotal: number; // Sum of fractional_share_remainders (display only, NOT in total)

 // Metadata
 userId: number;
 username?: string;
 calculatedAt: string;
}

/**
 * Calculate user's total shares from transaction records (source of truth)
 * This is the ONLY function that should be used to calculate share balances!
 */
export async function calculateUserShares(userId: number): Promise<ShareBreakdown> {
 // Fetch all raw data from server-side API (parallel queries)
 const data = await callShareCalcAPI('calculate_user_shares', { userId });

 let directPurchases = 0;
 let bonusShares = 0;
 let commissionLevelShares = 0;

 data.purchases?.forEach((p: any) => {
 const shares = parseFloat(String(p.shares_purchased || 0));
 const bonus = parseFloat(String(p.bonus_shares || 0));

 if (p.payment_method?.startsWith('COMMISSION_LEVEL')) {
 commissionLevelShares += shares;
 } else {
 directPurchases += shares;
 }
 bonusShares += bonus;
 });

 const presaleCommissionShares = data.commissionTx?.reduce((sum: number, c: any) =>
 sum + parseFloat(String(c.share_commission || 0)), 0) || 0;

 const convertedShares = data.conversions?.reduce((sum: number, c: any) =>
 sum + parseFloat(String(c.shares_requested || 0)), 0) || 0;

 const transfersReceived = data.received?.reduce((sum: number, t: any) =>
 sum + parseFloat(String(t.shares_transferred || 0)), 0) || 0;

 const transfersSent = data.sent?.reduce((sum: number, t: any) =>
 sum + parseFloat(String(t.shares_transferred || 0)), 0) || 0;

 const legacyShares = parseFloat(String(data.commissionBalance?.legacy_shares || 0));
 // commission_balances.share_balance is NOT added to total.
 // Those commission shares are already represented as COMMISSION_LEVEL_* records
 // in aureus_share_purchases. Adding share_balance would double-count them.
 // The DB view user_share_balances is the source of truth.

 // Calculate totals — NO commission_balances.share_balance to avoid double-counting
 // NOTE: fractional_share_remainders are EXCLUDED from totalShares — display only
 const netTransfers = transfersReceived - transfersSent;
 // NOTE: directPurchases (shares_purchased) already includes bonus shares, so do NOT add bonusShares again
 const totalShares = directPurchases + commissionLevelShares +
 presaleCommissionShares + convertedShares + legacyShares + netTransfers;
 const wholeShares = Math.floor(totalShares);
 const fractionalShares = totalShares - wholeShares;

 // Fractional remainders from consolidation (NOT counted in totalShares)
 const fractionalRemainderTotal = data.fractionalRemainders?.reduce((sum: number, r: any) =>
 sum + parseFloat(String(r.shares_remainder || 0)), 0) || 0;

 const breakdown: ShareBreakdown = {
 directPurchases,
 bonusShares,
 commissionLevelShares,
 presaleCommissionShares,
 convertedShares,
 transfersReceived,
 transfersSent,
 netTransfers,
 legacyShares,
 totalShares,
 wholeShares,
 fractionalShares,
 fractionalRemainderTotal,
 userId,
 username: data.user?.username,
 calculatedAt: new Date().toISOString()
 };

 return breakdown;
}

/**
 * Get just the total shares for a user (convenience method)
 */
export async function getUserTotalShares(userId: number): Promise<number> {
 const breakdown = await calculateUserShares(userId);
 return breakdown.totalShares;
}

/**
 * Get just the whole shares for a user (for certificate generation)
 */
export async function getUserWholeShares(userId: number): Promise<number> {
 const breakdown = await calculateUserShares(userId);
 return breakdown.wholeShares;
}

/**
 * Calculate shares for multiple users at once (batch operation)
 */
export async function calculateMultipleUserShares(userIds: number[]): Promise<Map<number, ShareBreakdown>> {
 const results = new Map<number, ShareBreakdown>();

 // Process in parallel for efficiency
 const promises = userIds.map(async (userId) => {
 const breakdown = await calculateUserShares(userId);
 results.set(userId, breakdown);
 });

 await Promise.all(promises);
 return results;
}

/**
 * Format share breakdown for display
 */
export function formatShareBreakdown(breakdown: ShareBreakdown): string {
 return `
 Share Breakdown for ${breakdown.username || `User ${breakdown.userId}`}
────────────────────────────────────────
Direct Purchases: ${breakdown.directPurchases.toFixed(4)} shares
Bonus Shares: ${breakdown.bonusShares.toFixed(4)} shares
Commission Level: ${breakdown.commissionLevelShares.toFixed(4)} shares
Presale Commission: ${breakdown.presaleCommissionShares.toFixed(4)} shares
Converted Shares: ${breakdown.convertedShares.toFixed(4)} shares
Transfers Received: +${breakdown.transfersReceived.toFixed(4)} shares
Transfers Sent: -${breakdown.transfersSent.toFixed(4)} shares
Legacy Adjustment: ${breakdown.legacyShares.toFixed(4)} shares
────────────────────────────────────────
TOTAL: ${breakdown.totalShares.toFixed(4)} shares
Whole Shares: ${breakdown.wholeShares} shares
Fractional (in source tables): ${breakdown.fractionalShares.toFixed(4)} shares
Fractional Remainders (consolidated): ${breakdown.fractionalRemainderTotal.toFixed(4)} shares
────────────────────────────────────────
Calculated: ${breakdown.calculatedAt}
 `.trim();
}

/**
 * DIVIDEND ELIGIBILITY - 3-MONTH HOLDING RULE (May 2026 update)
 * ==============================================================
 * To qualify for any quarterly dividend payout, shares must be held for at
 * least 3 months *prior to the payout's calendar month*. The cutoff date is
 * the 6th day of the month that falls 3 months before the payout month.
 *
 * Examples:
 *   July 2026 payout    → must have purchased on/before April 6, 2026
 *   October 2026 payout → must have purchased on/before July 6, 2026
 *   January 2027 payout → must have purchased on/before October 6, 2026
 *   April 2027 payout   → must have purchased on/before January 6, 2027
 *
 * The first scheduled payout under the revised structure is July 2026.
 * DIVIDEND_CUTOFF_DATE below is retained as the July-2026 cutoff for any
 * existing call-sites that reference it, but new code should call
 * getEligibilityCutoffForPayout(year, month).
 */
export const DIVIDEND_CUTOFF_DATE = new Date('2026-04-06T00:00:00Z');

const MONTH_NAME_TO_INDEX: Record<string, number> = {
 January: 0, February: 1, March: 2, April: 3, May: 4, June: 5,
 July: 6, August: 7, September: 8, October: 9, November: 10, December: 11,
};

/**
 * Returns the latest date (inclusive) by which a share must have been
 * purchased to qualify for the given payout, per the 3-month holding rule.
 */
export function getEligibilityCutoffForPayout(year: number, month: string): Date {
 const monthIdx = MONTH_NAME_TO_INDEX[month];
 if (monthIdx === undefined) {
 throw new Error(`Unknown payout month: ${month}`);
 }
 // Cutoff = 6th day of (payout month - 3 months), UTC
 const cutoff = new Date(Date.UTC(year, monthIdx, 6));
 cutoff.setUTCMonth(cutoff.getUTCMonth() - 3);
 return cutoff;
}

/**
 * Returns true when the given purchase date qualifies for the payout under
 * the 3-month holding rule.
 */
export function isEligibleForPayout(
 firstPurchaseDate: Date | null,
 year: number,
 month: string,
): boolean {
 if (!firstPurchaseDate) return false;
 const cutoff = getEligibilityCutoffForPayout(year, month);
 // Cutoff is inclusive (≤). Compare via UTC ms to avoid TZ drift.
 return firstPurchaseDate.getTime() <= cutoff.getTime() + (24 * 60 * 60 * 1000 - 1);
}

/**
 * Get the earliest purchase date for a user
 * This includes all share acquisitions: direct purchases, commission shares, conversions, transfers
 */
export async function getFirstPurchaseDate(userId: number): Promise<Date | null> {
 const data = await callShareCalcAPI('get_first_purchase_date', { userId });

 const dates: Date[] = [];

 if (data.purchases?.[0]?.created_at) dates.push(new Date(data.purchases[0].created_at));
 if (data.commissions?.[0]?.created_at) dates.push(new Date(data.commissions[0].created_at));
 if (data.conversions?.[0]?.created_at) dates.push(new Date(data.conversions[0].created_at));
 if (data.transfers?.[0]?.completed_at) dates.push(new Date(data.transfers[0].completed_at));

 if (dates.length === 0) return null;

 return dates.reduce((earliest, current) =>
 current < earliest ? current : earliest
 );
}

export interface DividendEligibility {
 firstPurchaseDate: Date | null;
 /** True when the user qualifies for the very first (July 2026) payout. */
 purchasedBeforeCutoff: boolean;
 /** Human-readable date of the first payout the user qualifies for, e.g. "July 2026". */
 firstDividendDate: string;
 firstDividendMonth: 'July' | 'October';
 firstDividendYear: number;
 /** Subset of 2026 payouts the user qualifies for under the 3-month rule. */
 eligibleFor2026Payouts: ('July' | 'October')[];
 /** Holding-rule cutoff date for the first scheduled (July 2026) payout. */
 firstPayoutCutoffDate: Date;
}

/**
 * Determine a user's dividend eligibility under the 3-month holding rule.
 * Replaces the prior April-2026-flooding logic.
 */
export async function getDividendEligibility(userId: number): Promise<DividendEligibility> {
 const firstPurchaseDate = await getFirstPurchaseDate(userId);

 const eligibleForJuly = isEligibleForPayout(firstPurchaseDate, 2026, 'July');
 const eligibleForOctober = isEligibleForPayout(firstPurchaseDate, 2026, 'October');

 const eligibleFor2026Payouts: ('July' | 'October')[] = [];
 if (eligibleForJuly) eligibleFor2026Payouts.push('July');
 if (eligibleForOctober) eligibleFor2026Payouts.push('October');

 const firstDividendMonth: 'July' | 'October' = eligibleForJuly ? 'July' : 'October';
 const firstDividendDate = eligibleForJuly
 ? 'July 2026'
 : eligibleForOctober
 ? 'October 2026'
 : 'January 2027';
 const firstDividendYear = eligibleForJuly || eligibleForOctober ? 2026 : 2027;

 return {
 firstPurchaseDate,
 purchasedBeforeCutoff: eligibleForJuly,
 firstDividendDate,
 firstDividendMonth,
 firstDividendYear,
 eligibleFor2026Payouts,
 firstPayoutCutoffDate: getEligibilityCutoffForPayout(2026, 'July'),
 };
}

/**
 * Calculate user's projected dividends for 2026 based on the user's eligible
 * payouts (per the 3-month holding rule).
 */
export function calculate2026DividendsForUser(
 shares: number,
 eligiblePayouts: ('July' | 'October')[]
): { total: number; breakdown: { month: string; amount: number }[] } {
 // 2026 quarterly payout-per-share (static 1.4M-denominator floor; dynamic
 // denominator scaling for early payouts is applied separately by the
 // calculator UIs via getDynamicPayoutPerShare()).
 const payoutAmounts: Record<'July' | 'October', number> = {
 'July': 20,
 'October': 25,
 };

 const breakdown = eligiblePayouts.map(month => ({
 month: `${month} 2026`,
 amount: payoutAmounts[month] * shares,
 }));

 const total = breakdown.reduce((sum, p) => sum + p.amount, 0);

 return { total, breakdown };
}

